// [ANÁLISIS] Archivo: AdminDashboard.jsx
// [IMPACTO] Afecta a: Dashboard (Kennedy/Pepe) - Introducción de Modo Claro.
// [RAZÓN] Requerimiento del usuario para alternar entre temas.
// [FECHA] 2026-03-23

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { usePersistedAuth } from '../hooks/usePersistedAuth';
import {
  LayoutDashboard,
  Calendar as CalendarIcon,
  Users,
  Scissors,
  UserCog,
  Settings,
  TrendingUp,
  TrendingDown,
  Clock,
  Plus,
  Ban,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Activity,
  CheckCircle2,
  Phone,
  Check,
  RefreshCw,
  PieChart as PieChartIcon,
  Trash2,
  Edit,
  Menu,
  User,
  RotateCw,
  Sun,
  Moon
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ScrollArea, ScrollBar } from './ui/scroll-area.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  format, addDays, subDays,
  isToday, isBefore, isAfter, startOfDay, endOfDay,
  parseISO, isSameDay, addMinutes, isValid,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  addMonths, subMonths, addWeeks, subWeeks
} from 'date-fns';
import { es } from 'date-fns/locale';
import { useMonthlyRevenue } from '../hooks/useMonthlyRevenue';
import TopServices from './dashboard/TopServices';
import '../styles/stats.css';

const AVATAR_OPTIONS = [
  'Admin', 'Felix', 'Aneka', 'Jack', 'Oliver', 'Sophia', 'Milo', 'Luna', 'Leo',
  'Bella', 'Charlie', 'Oscar', 'Mia', 'kennedy', 'Pepe', 'Luis', 'Carlos', 'David'
].map(seed => `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`);

// ============================================
// CONFIGURACIÓN (SINGLE BARBER MODE)
// ============================================

const RefreshIcon = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>
  </svg>
);

const WifiIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.59 16a6 6 0 016.82 0M12 20h.01"/>
  </svg>
);

const WifiOffIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.58 9M1.42 9a15.91 15.91 0 014.7-2.88M8.59 16a6 6 0 016.82 0M12 20h.01"/>
  </svg>
);

const COLORS = ['#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#ef4444'];

const BARBEROS = [
  { id: 1, nombre: 'kennedy', color: '#f59e0b', avatar: AVATAR_OPTIONS[13] },
];

// ============================================
// COMPONENTES AUXILIARES
// ============================================

const Sidebar = ({ activeTab, setActiveTab, adminAvatar, setAvatarModalOpen }) => {
  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'agenda', icon: CalendarIcon, label: 'Agenda' },
    { id: 'clientes', icon: Users, label: 'Clientes' },
    { id: 'configuracion', icon: Settings, label: 'Configuración' },
  ];

  return (
    <aside className="fixed left-0 top-0 z-40 flex h-screen w-[72px] flex-col border-r border-[var(--border-main)] bg-[var(--bg-sidebar)] backdrop-blur-md transition-colors duration-300">
      <div className="flex h-16 items-center justify-center border-b border-[var(--border-main)]">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500">
          <Scissors className="h-5 w-5 text-black" />
        </div>
      </div>
      <nav className="flex flex-1 flex-col items-center gap-2 py-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`group relative flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200 ${isActive
                ? 'bg-[var(--bg-nav-active)] text-[var(--text-nav-active)] shadow-[0_0_15px_rgba(245,158,11,0.3)]'
                : 'text-[var(--text-nav-inactive)] hover:bg-[var(--bg-item-hover)] hover:text-[var(--text-main)]'
                }`}
              title={item.label}
            >
              <Icon className="h-5 w-5" />
            </button>
          );
        })}
      </nav>
      <div className="flex items-center justify-center border-t border-[var(--border-main)] py-4">
        <button onClick={() => setAvatarModalOpen(true)} className="outline-none focus:ring-2 focus:ring-amber-500 rounded-full">
          <Avatar className="h-9 w-9 ring-2 ring-[var(--border-main)] hover:ring-amber-500 transition-all cursor-pointer">
            <AvatarImage src={adminAvatar} />
            <AvatarFallback>AD</AvatarFallback>
          </Avatar>
        </button>
      </div>
    </aside>
  );
};

const Skeleton = ({ className }) => (
  <div className={`animate-pulse rounded-md bg-zinc-800 ${className}`} />
);

const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, variant = 'default', children, isLoading, onClick }) => {
  const variantStyles = {
    default: 'border-[var(--border-main)] bg-[var(--bg-card)]',
    success: 'border-emerald-500/20 bg-emerald-500/5',
    warning: 'border-amber-500/20 bg-amber-500/5',
  };

  if (isLoading) {
    return (
      <div className={`group relative overflow-hidden rounded-xl border p-5 backdrop-blur-sm ${variantStyles[variant]}`}>
        <div className="flex items-start justify-between">
          <div className="space-y-2 w-full">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-10 w-10 rounded-lg" />
        </div>
        <div className="mt-4">
          <Skeleton className="h-2 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden rounded-xl border p-5 backdrop-blur-sm transition-all duration-300 hover:border-amber-500/30 ${variantStyles[variant]} ${onClick ? 'cursor-pointer' : ''}`}
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">{title}</p>
          <p className="text-2xl font-bold tracking-tight text-white">{value}</p>
          {subtitle && <p className="text-sm text-zinc-500">{subtitle}</p>}
        </div>
        {Icon && (
          <div className="rounded-lg bg-zinc-800 p-2.5 text-zinc-400 group-hover:bg-amber-500/10 group-hover:text-amber-500">
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

const SkeletonCalendar = () => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 space-y-4">
    <div className="flex justify-between items-center">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-8 w-32" />
    </div>
    <div className="grid grid-cols-7 gap-px bg-zinc-800 rounded-lg overflow-hidden border border-zinc-800">
      {Array.from({ length: 35 }).map((_, i) => (
        <div key={i} className="bg-zinc-950 p-2 min-h-[100px]">
          <Skeleton className="h-4 w-6 mb-2" />
          <Skeleton className="h-3 w-full opacity-50" />
        </div>
      ))}
    </div>
  </div>
);

// ============================================
// VISTA DE CLIENTES
// ============================================
const ClientsView = ({ citas, servicios }) => {
  const clientsData = useMemo(() => {
    const clients = {};
    const now = new Date();

    citas.forEach(cita => {
      // Normalize name to group clients
      const nameKey = cita.summary.trim().toLowerCase();

      if (!clients[nameKey]) {
        clients[nameKey] = {
          name: cita.summary,
          phone: cita.phone_guess || "Desconocido",
          totalVisits: 0,
          futureVisits: 0,
          services: [],
          lastVisit: null
        };
      }

      clients[nameKey].totalVisits++;

      if (new Date(cita.fecha) > now) {
        clients[nameKey].futureVisits++;
      } else {
        // Determine service (naive)
        const likelyService = servicios?.find(s => cita.summary.toLowerCase().includes(s.nombre.toLowerCase()))?.nombre || "Corte Estándar";
        clients[nameKey].services.push(likelyService);
      }
    });

    return Object.values(clients).map(client => {
      // Frequency logic
      let frequency = "Nuevo";
      if (client.totalVisits > 5) frequency = "VIP";
      else if (client.totalVisits > 2) frequency = "Regular";

      // Most common service
      const commonService = client.services.length > 0 ? client.services.sort((a, b) =>
        client.services.filter(v => v === a).length - client.services.filter(v => v === b).length
      ).pop() : "Corte Estándar";

      return { ...client, frequency, commonService };
    }).sort((a, b) => b.totalVisits - a.totalVisits); // Sort by most frequent
  }, [citas, servicios]);

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Cartera de Clientes</h2>
          <p className="text-zinc-500 text-sm">Generado automáticamente de {citas.length} citas</p>
        </div>
        <div className="bg-zinc-900/50 px-4 py-2 rounded-lg border border-zinc-800 text-sm text-zinc-400">
          Total Clientes Únicos: <span className="text-white font-bold">{clientsData.length}</span>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-950/50 text-zinc-500 font-medium">
            <tr>
              <th className="px-6 py-4">Cliente</th>
              <th className="px-6 py-4">Frecuencia</th>
              <th className="px-6 py-4 text-center">Citas Totales</th>
              <th className="px-6 py-4 text-center">Futuras</th>
              <th className="px-6 py-4">Servicio Habitual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50 text-zinc-300">
            {clientsData.map((client, i) => (
              <tr key={i} className="hover:bg-zinc-800/20 transition-colors">
                <td className="px-6 py-4">
                  <div className="font-medium text-white">{client.name}</div>
                  <div className="text-xs text-zinc-500">{client.phone}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${client.frequency === 'VIP' ? 'bg-amber-400/10 text-amber-500 ring-amber-400/20' :
                    client.frequency === 'Regular' ? 'bg-blue-400/10 text-blue-400 ring-blue-400/20' :
                      'bg-zinc-400/10 text-zinc-400 ring-zinc-400/20'
                    }`}>
                    {client.frequency}
                  </span>
                </td>
                <td className="px-6 py-4 text-center font-mono">{client.totalVisits}</td>
                <td className="px-6 py-4 text-center">
                  {client.futureVisits > 0 ? (
                    <span className="text-emerald-500 font-bold">+{client.futureVisits}</span>
                  ) : '-'}
                </td>
                <td className="px-6 py-4 text-zinc-400">{client.commonService}</td>
              </tr>
            ))}
            {clientsData.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-zinc-500">
                  No hay datos de clientes suficientes aún.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// CALENDARIO MEJORADO (SINGLE VIEW)
// ============================================
const CalendarHeader = ({ currentDate, view, onViewChange, onNavigate, onToday }) => {
  return (
    <div className="flex items-center justify-between mb-4 bg-[var(--bg-card)] p-3 rounded-lg border border-[var(--border-main)] transition-colors duration-300" style={{ boxShadow: 'var(--shadow-card)' }}>
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-bold text-[var(--text-main)] capitalize min-w-[200px]">
          {format(currentDate, view === 'day' ? "EEEE, d MMMM" : "MMMM yyyy", { locale: es })}
        </h2>
        <div className="flex items-center bg-[var(--bg-toggle)] rounded-lg p-1 border border-[var(--border-main)]">
          <Button variant="ghost" size="icon" onClick={() => onNavigate('prev')} className="h-7 w-7 text-[var(--text-muted)] hover:text-[var(--text-main)]"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={onToday} className="h-7 px-3 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)] font-medium">Hoy</Button>
          <Button variant="ghost" size="icon" onClick={() => onNavigate('next')} className="h-7 w-7 text-[var(--text-muted)] hover:text-[var(--text-main)]"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex bg-[var(--bg-toggle)] rounded-lg p-1 border border-[var(--border-main)]">
        {['day', 'week', 'month'].map(v => (
          <button
            key={v}
            onClick={() => onViewChange(v)}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${view === v ? 'bg-[var(--bg-toggle-active)] text-[var(--text-toggle-active)] shadow-sm' : 'text-[var(--text-toggle)] hover:text-[var(--text-main)]'
              }`}
          >
            {v === 'day' ? 'Día' : v === 'week' ? 'Semana' : 'Mes'}
          </button>
        ))}
      </div>
    </div>
  );
};

const TimelineCalendar = ({ citas, currentDate, view, onSlotClick, onEventClick }) => {
  const horas = Array.from({ length: 13 }, (_, i) => `${(i + 9).toString().padStart(2, '0')}:00`);

  // HELPER: Position for event
  const getTopPosition = (date) => {
    const h = date.getHours();
    const m = date.getMinutes();
    if (h < 9) return 0;
    return ((h - 9) * 60 + m) * (64 / 60);
  };

  // --- DAY VIEW ---
  if (view === 'day') {
    const citasDelDia = citas.filter(c => isSameDay(c.fecha, currentDate));

    return (
      <div className="flex flex-col rounded-xl border border-[var(--border-main)] bg-[var(--bg-card)] backdrop-blur-sm h-[600px] overflow-hidden transition-colors duration-300" style={{ boxShadow: 'var(--shadow-card)' }}>
        <div className="flex border-b border-[var(--border-main)]">
          <div className="w-16 shrink-0 border-r border-[var(--border-main)] bg-[var(--bg-cal-header)] p-3"></div>
          {BARBEROS.map(barbero => (
            <div key={barbero.id} className="flex flex-1 items-center justify-center gap-2 bg-[var(--bg-cal-header)] p-3">
              <Avatar className="h-7 w-7">
                <AvatarImage src={barbero.avatar} />
                <AvatarFallback style={{ backgroundColor: barbero.color }}>{barbero.nombre[0]}</AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium text-[var(--text-main)]">{barbero.nombre}</span>
            </div>
          ))}
        </div>

        <ScrollArea className="h-full">
          <div className="relative flex min-h-[850px]">
            <div className="w-16 shrink-0 border-r border-[var(--border-main)] bg-[var(--bg-time-col)]">
              {horas.map(hora => (
                <div key={hora} className="flex h-16 items-start justify-end border-b border-[var(--border-light)] pr-2 pt-1 text-[10px] text-[var(--text-muted)] font-medium">
                  {hora}
                </div>
              ))}
            </div>

            <div className="relative flex-1 bg-[var(--bg-slot)]" onClick={() => onSlotClick(currentDate)}>
              {horas.map(hora => <div key={hora} className="h-16 border-b border-[var(--border-light)] hover:bg-[var(--bg-slot-hover)]" />)}

              {citasDelDia.map(cita => {
                const top = getTopPosition(cita.fecha);
                const isPast = isBefore(cita.fecha, new Date());
                const height = 30 * (64 / 60);

                return (
                  <div
                    key={cita.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(cita); }}
                    className={`absolute left-2 right-2 rounded-md border px-3 py-2 text-xs font-medium shadow-sm cursor-pointer hover:brightness-110 transition-all ${isPast
                      ? 'bg-[var(--bg-event-past)] border-[var(--border-event-past)] text-[var(--text-event-past)]'
                      : 'bg-amber-500/20 border-amber-500/40 text-amber-600'
                      }`}
                    style={{ top: `${top}px`, height: `${height}px` }}
                  >
                    <div className="font-bold flex justify-between">
                      <span className="truncate">{cita.summary}</span>
                      <span className="truncate ml-2">{format(cita.fecha, 'HH:mm')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  // --- WEEK VIEW ---
  if (view === 'week') {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));

    return (
      <div className="flex flex-col rounded-xl border border-[var(--border-main)] bg-[var(--bg-card)] backdrop-blur-sm h-[600px] overflow-hidden transition-colors duration-300" style={{ boxShadow: 'var(--shadow-card)' }}>
        <div className="flex border-b border-[var(--border-main)]">
          <div className="w-16 shrink-0 border-r border-[var(--border-main)] bg-[var(--bg-cal-header)]"></div>
          {days.map(day => (
            <div key={day.toISOString()} className={`flex-1 text-center py-3 border-r border-[var(--border-light)] ${isToday(day) ? 'bg-amber-500/10 text-amber-500' : 'bg-[var(--bg-cal-header)] text-[var(--text-muted)]'}`}>
              <div className="text-[10px] font-bold uppercase">{format(day, 'EEE', { locale: es })}</div>
              <div className={`text-lg font-bold ${isToday(day) ? 'text-amber-500' : 'text-[var(--text-main)]'}`}>{format(day, 'd')}</div>
            </div>
          ))}
        </div>

        <ScrollArea className="h-full">
          <div className="relative flex min-h-[850px]">
            <div className="w-16 shrink-0 border-r border-[var(--border-main)] bg-[var(--bg-time-col)]">
              {horas.map(hora => (
                <div key={hora} className="flex h-16 items-start justify-end border-b border-[var(--border-light)] pr-2 pt-1 text-[10px] text-[var(--text-muted)] font-medium">
                  {hora}
                </div>
              ))}
            </div>

            <div className="flex flex-1">
              {days.map(day => {
                const citasDia = citas.filter(c => isSameDay(c.fecha, day));
                return (
                  <div key={day.toISOString()} className="relative flex-1 border-r border-[var(--border-light)] bg-[var(--bg-slot)]" onClick={() => onSlotClick(day)}>
                    {horas.map(hora => <div key={hora} className="h-16 border-b border-[var(--border-light)] hover:bg-[var(--bg-slot-hover)]" />)}

                    {citasDia.map(cita => {
                      const top = getTopPosition(cita.fecha);
                      const isPast = isBefore(cita.fecha, new Date());
                      const height = 30 * (64 / 60);

                      return (
                        <div
                          key={cita.id}
                          onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(cita); }}
                          className={`absolute left-1 right-1 rounded sm:left-2 sm:right-2 border p-1 text-[10px] sm:text-xs font-medium shadow-sm cursor-pointer hover:brightness-110 overflow-hidden ${isPast
                            ? 'bg-[var(--bg-event-past)] border-[var(--border-event-past)] text-[var(--text-event-past)]'
                            : 'bg-amber-500/20 border-amber-500/40 text-amber-600'
                            }`}
                          style={{ top: `${top}px`, height: `${height}px` }}
                        >
                          <span className="block truncate">{format(cita.fecha, 'HH:mm')}</span>
                          <span className="block truncate font-bold">{cita.summary}</span>
                        </div>
                      );
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </ScrollArea>
      </div>
    )
  }

  // --- MONTH VIEW ---
  if (view === 'month') {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    // Headers
    const weekDays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const cloneDay = day;
        const isMonth = isSameDay(day, monthStart) || (day >= monthStart && day <= monthEnd);
        const citasDia = citas.filter(c => isSameDay(c.fecha, cloneDay));

        days.push(
          <div
            key={day.toString()}
            className={`min-h-[100px] border border-[var(--border-light)] p-2 cursor-pointer transition-colors hover:bg-[var(--bg-slot-hover)] ${!isMonth ? "bg-[var(--bg-month-off)] text-[var(--text-month-off)]" : "bg-[var(--bg-month-on)] text-[var(--text-main)]"
              } ${isToday(day) ? "ring-1 ring-inset ring-amber-500" : ""}`}
            onClick={() => onSlotClick(cloneDay)}
          >
            <div className="flex justify-between items-start mb-1">
              <span className={`text-sm font-bold ${isToday(day) ? 'text-amber-500' : ''}`}>{formattedDate}</span>
              {citasDia.length > 0 && <span className="text-[10px] bg-[var(--bg-badge)] px-1.5 rounded-full text-[var(--text-badge)]">{citasDia.length}</span>}
            </div>
            <div className="space-y-1">
              {citasDia.slice(0, 3).map(c => (
                <div
                  key={c.id}
                  onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(c); }}
                  className="text-[10px] truncate rounded px-1 py-0.5 bg-amber-500/10 text-amber-500 border border-amber-500/20 hover:brightness-110"
                >
                  {format(c.fecha, 'HH:mm')} {c.summary}
                </div>
              ))}
              {citasDia.length > 3 && (
                <div className="text-[10px] text-[var(--text-muted)] text-center italic">
                  + {citasDia.length - 3} más
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7 w-full">
          {days}
        </div>
      );
      days = [];
    }

    return (
      <div className="rounded-xl border border-[var(--border-main)] bg-[var(--bg-card)] backdrop-blur-sm overflow-hidden transition-colors duration-300" style={{ boxShadow: 'var(--shadow-card)' }}>
        <div className="grid grid-cols-7 border-b border-[var(--border-main)] bg-[var(--bg-cal-header)]">
          {weekDays.map(d => (
            <div key={d} className="py-2 text-center text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
              {d}
            </div>
          ))}
        </div>
        <div>{rows}</div>
      </div>
    );
  }

  return null;
};

// ============================================
// MODALES
// ============================================
const NuevaReservaModal = ({ open, onOpenChange, barberos, servicios, onConfirm, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '10:00',
    serviceId: '',
    barberoId: ''
  });

  const handleSubmit = () => {
    const servicio = servicios.find(s => s.id === parseInt(formData.serviceId));
    if (!formData.name || !formData.phone || !servicio) {
      toast.error("Rellena todos los campos");
      return;
    }

    const payload = {
      name: formData.name,
      phone: formData.phone,
      date: `${formData.date} ${formData.time}:00`,
      services: servicio.nombre,
      total_price: servicio.precio,
      duration: servicio.duracion,
      user_id: `WEB-${Date.now()}`
    };
    onConfirm(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[var(--border-main)] bg-[var(--bg-card)] text-[var(--text-main)] sm:max-w-[425px] shadow-[var(--shadow-lg)]">
        <DialogHeader>
          <DialogTitle className="text-[var(--text-main)]">Nueva Reserva</DialogTitle>
          <DialogDescription className="text-[var(--text-secondary)]">Crear cita manual en el sistema.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label className="text-[var(--text-secondary)]">Cliente</Label>
            <Input
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre completo"
              className="bg-[var(--bg-card-inner)] border-[var(--border-main)] text-[var(--text-main)] focus:border-amber-500"
            />
          </div>
          <div className="grid gap-2">
            <Label className="text-[var(--text-secondary)]">Teléfono</Label>
            <Input
              value={formData.phone}
              onChange={e => setFormData({ ...formData, phone: e.target.value })}
              placeholder="600000000"
              className="bg-[var(--bg-card-inner)] border-[var(--border-main)] text-[var(--text-main)] focus:border-amber-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-2">
              <Label className="text-[var(--text-secondary)]">Fecha</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={e => setFormData({ ...formData, date: e.target.value })}
                className="bg-[var(--bg-card-inner)] border-[var(--border-main)] text-[var(--text-main)] focus:border-amber-500"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-[var(--text-secondary)]">Hora</Label>
              <Input
                type="time"
                value={formData.time}
                onChange={e => setFormData({ ...formData, time: e.target.value })}
                className="bg-[var(--bg-card-inner)] border-[var(--border-main)] text-[var(--text-main)] focus:border-amber-500"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label className="text-[var(--text-secondary)]">Servicio</Label>
            <Select onValueChange={v => setFormData({ ...formData, serviceId: v })}>
              <SelectTrigger className="bg-[var(--bg-card-inner)] border-[var(--border-main)] text-[var(--text-main)] h-11">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-card)] border-[var(--border-main)] text-[var(--text-main)]">
                {servicios.map(s => (
                  <SelectItem key={s.id} value={s.id.toString()}>{s.nombre} ({s.precio}€)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[var(--border-main)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]">Cancelar</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-amber-500 text-black hover:bg-amber-600 font-bold">
            {isSubmitting ? 'Guardando...' : 'Confirmar Reserva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// MODAL DETALLE CITA (NEW)
// ============================================
const DetalleCitaModal = ({ open, onOpenChange, cita, onCancel, isCanceling }) => {
  if (!cita) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-zinc-800 bg-zinc-950 text-white sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Detalles de la Cita</DialogTitle>
          <DialogDescription>Gestionar cita existente.</DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <div className="flex items-center gap-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500">
              <UserCog className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-400">Cliente</p>
              <p className="text-lg font-bold text-white">{cita.summary}</p>
              <p className="text-xs text-zinc-500">{cita.phone_guess || "Teléfono no detectado"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-zinc-900/30 border border-zinc-800">
              <p className="text-xs text-zinc-500 mb-1">Fecha</p>
              <div className="flex items-center gap-2 text-white">
                <CalendarIcon className="h-4 w-4 text-amber-500" />
                <span className="font-medium">{format(cita.fecha, 'dd MMM yyyy', { locale: es })}</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-zinc-900/30 border border-zinc-800">
              <p className="text-xs text-zinc-500 mb-1">Hora</p>
              <div className="flex items-center gap-2 text-white">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="font-medium">{format(cita.fecha, 'HH:mm')}</span>
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-xs">
            ⚠️ Para cambiar la hora, cancela esta cita y crea una nueva.
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-800 hover:bg-zinc-900">Cerrar</Button>
          <Button
            onClick={() => onCancel(cita)}
            disabled={isCanceling}
            variant="destructive"
            className="bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/20"
          >
            {isCanceling ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Cancelando...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" /> Cancelar Cita
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// CONFIGURACIÓN UI
// ============================================
const ConfigurationView = ({
  adminKey,
  barberos,
  fetchBarberos,
  barberoModal,
  setBarberoModal,
  deleteModal,
  setDeleteModal,
  bForm,
  setBForm,
  bSaving,
  setBSaving,
  servicios,
  fetchServicios,
  servicioModal,
  setServicioModal,
  sForm,
  setSForm,
  sSaving,
  setSSaving
}) => {
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // --- DRAG & DROP STATE ---
  const dragItem = useRef();
  const dragOverItem = useRef();
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    fetchBarberos();
  }, [adminKey, fetchBarberos]); // Added fetchBarberos to dependencies

  // --- BARBEROS CRUD ---
  const openAddBarbero = () => { setBForm({ nombre: '', activo: true, avatar: AVATAR_OPTIONS[0], password: '' }); setBarberoModal({ mode: 'add' }); };
  const openEditBarbero = (b) => { setBForm({ nombre: b.nombre, activo: b.activo !== false, avatar: b.avatar || AVATAR_OPTIONS[0], password: b.password || '' }); setBarberoModal({ mode: 'edit', id: b.id }); };

  const saveBarbero = async () => {
    if (!bForm.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setBSaving(true);
    try {
      const payload = {
        nombre: bForm.nombre.trim(),
        activo: bForm.activo,
        avatar: bForm.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(bForm.nombre)}`,
        color: '#f59e0b'
      };

      if (bForm.password && bForm.password.trim()) {
        payload.password = bForm.password.trim();
      }

      let r;
      if (barberoModal.mode === 'add') {
        r = await fetch('/api/admin/barberos', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }, body: JSON.stringify(payload) });
      } else {
        r = await fetch(`/api/admin/barberos/${barberoModal.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }, body: JSON.stringify(payload) });
      }
      const d = await r.json();
      if (d.status === 'ok') {
        toast.success(barberoModal.mode === 'add' ? 'Barbero añadido' : 'Barbero actualizado');
        setBarberoModal(null);
        fetchBarberos();
      } else { toast.error(d.message || 'Error'); }
    } catch { toast.error('Error de conexión'); }
    setBSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      const url = deleteModal.type === 'barbero'
        ? `/api/admin/barberos/${deleteModal.id}`
        : `/api/admin/servicios/${deleteModal.id}`;
      const r = await fetch(url, { method: 'DELETE', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey } });
      const d = await r.json();
      if (d.status === 'ok') {
        toast.success('Eliminado correctamente');
        deleteModal.type === 'barbero' ? fetchBarberos() : fetchServicios();
      } else { toast.error(d.message || 'Error'); }
    } catch { toast.error('Error de conexión'); }
    setDeleteModal(null);
  };

  // --- SERVICIOS CRUD ---
  const openAddServicio = () => { setSForm({ nombre: '', precio: '', duracion: '', descripcion: '' }); setServicioModal({ mode: 'add' }); };
  const openEditServicio = (s) => { setSForm({ nombre: s.nombre, precio: String(s.precio), duracion: String(s.duracion), descripcion: s.descripcion || '' }); setServicioModal({ mode: 'edit', id: s.id }); };

  const saveServicio = async () => {
    if (!sForm.nombre.trim() || !sForm.precio || !sForm.duracion) { toast.error('Completa los campos obligatorios'); return; }
    setSSaving(true);
    try {
      const payload = {
        nombre: sForm.nombre.trim(),
        precio: parseFloat(sForm.precio),
        duracion: parseInt(sForm.duracion),
        descripcion: sForm.descripcion.trim() || `Servicio de ${sForm.nombre.trim()}`
      };
      let r;
      if (servicioModal.mode === 'add') {
        r = await fetch('/api/admin/servicios', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }, body: JSON.stringify(payload) });
      } else {
        r = await fetch(`/api/admin/servicios/${servicioModal.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }, body: JSON.stringify(payload) });
      }
      const d = await r.json();
      if (d.status === 'ok') {
        toast.success(servicioModal.mode === 'add' ? 'Servicio añadido' : 'Servicio actualizado');
        setServicioModal(null);
        fetchServicios();
      } else { toast.error(d.message || 'Error'); }
    } catch { toast.error('Error de conexión'); }
    setSSaving(false);
  };

  const handleSort = async () => {
    // Reorder array logically
    const _servicios = [...servicios];
    const draggedItemContent = _servicios.splice(dragItem.current, 1)[0];
    _servicios.splice(dragOverItem.current, 0, draggedItemContent);

    // Update local state and current ordering value
    const reorderedServicios = _servicios.map((s, index) => ({ ...s, orden: index }));

    // Solo actualizar frontend si fetchServicios no se ha disparado (hacemos set direct function down below, 
    // pero no podemos pq servicios viene del padre. Vamos a llamar en batch al final)

    dragItem.current = null;
    dragOverItem.current = null;
    setIsReordering(true);

    try {
      const payload = {
        servicios: reorderedServicios.map(s => ({ id: s.id, orden: s.orden }))
      };
      const r = await fetch('/api/admin/servicios/reorder', { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }, body: JSON.stringify(payload) });
      const d = await r.json();
      if (d.status === 'ok') {
        toast.success('Orden actualizado');
        fetchServicios(); // Refresh the parent state layout cleanly
      } else {
        toast.error('Error al actualizar orden');
        fetchServicios(); // Revert to DB original layout on fail
      }
    } catch {
      toast.error('Error de conexión');
      fetchServicios();
    }
    setIsReordering(false);
  };



  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-5">

      {/* MODAL BARBERO */}
      {barberoModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-all duration-300" onClick={() => setBarberoModal(null)}>
          <div className="bg-[var(--bg-card)] border border-[var(--border-main)] rounded-2xl p-6 w-full max-w-sm shadow-[var(--shadow-md)]" onClick={e => e.stopPropagation()}>
            <h3 className="text-[var(--text-main)] font-bold text-lg mb-4">{barberoModal.mode === 'add' ? '+ Añadir Barbero' : 'Editar Barbero'}</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-[var(--text-secondary)]">Nombre</Label>
                <Input value={bForm.nombre} onChange={e => setBForm(f => ({ ...f, nombre: e.target.value }))} className="bg-[var(--bg-card-inner)] border-[var(--border-main)] focus:border-amber-500 h-11 mt-1 text-[var(--text-main)]" placeholder="Nombre del barbero" />
              </div>
              <div>
                <Label className="text-[var(--text-secondary)]">Contraseña (Acceso Admin)</Label>
                <Input type="password" value={bForm.password || ''} onChange={e => setBForm(f => ({ ...f, password: e.target.value }))} className="bg-[var(--bg-card-inner)] border-[var(--border-main)] focus:border-amber-500 h-11 mt-1 text-[var(--text-main)]" placeholder="Completar para dar acceso" />
              </div>
              <div>
                <Label className="text-[var(--text-secondary)]">Avatar</Label>
                <ScrollArea className="w-full whitespace-nowrap rounded-md border border-[var(--border-main)] p-3 pb-4 mt-1 bg-[var(--bg-card-inner)]">
                  <div className="flex w-max space-x-4 p-1">
                    {AVATAR_OPTIONS.map(url => (
                      <img
                        key={url}
                        src={url}
                        className={`h-12 w-12 cursor-pointer rounded-full border-2 hover:scale-110 transition-transform flex-shrink-0 ${bForm.avatar === url ? 'border-amber-500 scale-110' : 'border-transparent'}`}
                        onClick={() => setBForm(f => ({ ...f, avatar: url }))}
                        alt="avatar"
                      />
                    ))}
                  </div>
                  <ScrollBar orientation="horizontal" className="h-2" />
                </ScrollArea>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <input type="checkbox" id="activo-cb" checked={bForm.activo} onChange={e => setBForm(f => ({ ...f, activo: e.target.checked }))} className="w-4 h-4 accent-amber-500" />
                <Label htmlFor="activo-cb" className="text-zinc-400 cursor-pointer">Activo</Label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setBarberoModal(null)}>Cancelar</Button>
              <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={saveBarbero} disabled={bSaving}>
                {bSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SERVICIO */}
      {servicioModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-all duration-300" onClick={() => setServicioModal(null)}>
          <div className="bg-[var(--bg-card)] border border-[var(--border-main)] rounded-2xl p-6 w-full max-w-sm shadow-[var(--shadow-md)]" onClick={e => e.stopPropagation()}>
            <h3 className="text-[var(--text-main)] font-bold text-lg mb-4">{servicioModal.mode === 'add' ? '+ Añadir Servicio' : 'Editar Servicio'}</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-[var(--text-secondary)]">Nombre</Label>
                <Input value={sForm.nombre} onChange={e => setSForm(f => ({ ...f, nombre: e.target.value }))} className="bg-[var(--bg-card-inner)] border-[var(--border-main)] focus:border-amber-500 h-11 mt-1 text-[var(--text-main)]" placeholder="Ej: Corte Clásico" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[var(--text-secondary)]">Precio (€)</Label>
                  <Input type="number" value={sForm.precio} onChange={e => setSForm(f => ({ ...f, precio: e.target.value }))} className="bg-[var(--bg-card-inner)] border-[var(--border-main)] focus:border-amber-500 h-11 mt-1 text-[var(--text-main)]" placeholder="12" />
                </div>
                <div>
                  <Label className="text-[var(--text-secondary)]">Duración (min)</Label>
                  <Input type="number" value={sForm.duracion} onChange={e => setSForm(f => ({ ...f, duracion: e.target.value }))} className="bg-[var(--bg-card-inner)] border-[var(--border-main)] focus:border-amber-500 h-11 mt-1 text-[var(--text-main)]" placeholder="30" />
                </div>
              </div>
              <div>
                <Label className="text-[var(--text-secondary)]">Descripción</Label>
                <Input value={sForm.descripcion} onChange={e => setSForm(f => ({ ...f, descripcion: e.target.value }))} className="bg-[var(--bg-card-inner)] border-[var(--border-main)] focus:border-amber-500 h-11 mt-1 text-[var(--text-main)]" placeholder="Breve descripción del servicio" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setServicioModal(null)}>Cancelar</Button>
              <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold" onClick={saveServicio} disabled={sSaving}>
                {sSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAR ELIMINAR */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setDeleteModal(null)}>
          <div className="bg-zinc-900 border border-red-800 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-2">¿Eliminar?</h3>
            <p className="text-zinc-400 mb-6">Se eliminará <span className="text-white font-bold">{deleteModal.nombre}</span>. Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setDeleteModal(null)}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold" onClick={confirmDelete}>Eliminar</Button>
            </div>
          </div>
        </div>
      )}



      <div className="grid gap-6 md:grid-cols-2">
        {/* EQUIPO */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Users className="text-amber-500" />
              <h3 className="text-lg font-bold text-white">Equipo</h3>
            </div>
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black" onClick={openAddBarbero}>
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>
          <div className="space-y-4">
            {barberos.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">No hay barberos. Añade uno.</p>
            ) : barberos.map(b => (
              <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-950/50">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={b.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${b.nombre}`} />
                    <AvatarFallback>{b.nombre?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-white">{b.nombre}</p>
                    <p className={`text-xs ${b.activo !== false ? 'text-green-500' : 'text-zinc-500'}`}>{b.activo !== false ? 'Activo' : 'Inactivo'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => openEditBarbero(b)}><Edit className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-red-500" onClick={() => setDeleteModal({ type: 'barbero', id: b.id, nombre: b.nombre })}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SERVICIOS */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Scissors className="text-amber-500" />
              <h3 className="text-lg font-bold text-white">Servicios</h3>
            </div>
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black" onClick={openAddServicio}>
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>
          <ScrollArea className="h-[300px] pr-4">
            <div className={`space-y-3 ${isReordering ? 'opacity-50 pointer-events-none' : ''}`}>
              {servicios.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-4">No hay servicios. Añade uno.</p>
              ) : servicios.map((s, index) => (
                <div
                  key={s.id}
                  draggable
                  onDragStart={(e) => dragItem.current = index}
                  onDragEnter={(e) => dragOverItem.current = index}
                  onDragEnd={handleSort}
                  onDragOver={(e) => e.preventDefault()}
                  className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-950/50 cursor-grab active:cursor-grabbing hover:border-amber-500/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Menu className="w-5 h-5 text-zinc-600 cursor-grab" />
                    <div>
                      <div className="flex items-center">
                        <p className="font-medium text-white">{s.nombre}</p>
                        <span className="text-sm font-bold bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded ml-2">{s.precio}€</span>
                        <span className="text-xs text-zinc-500 ml-2">({s.duracion} min)</span>
                      </div>
                      <div className="text-xs text-zinc-400 mt-1">{s.descripcion || `Servicio de ${s.nombre}`}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => openEditServicio(s)}><Edit className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-red-500" onClick={() => setDeleteModal({ type: 'servicio', id: s.id, nombre: s.nombre })}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================
export default function AdminDashboard({ onLogout }) {
  // AUTH HOOK
  const { user: authUser, token: authToken, login, logout, isAuthenticated: isAuth, isLoading: isAuthLoading } = usePersistedAuth();

  // AUTH STATE (Legacy compatibility if needed, but primary is the hook)
  const [authData, setAuthData] = useState({ user: '', pass: '' });
  const [authError, setAuthError] = useState(false);
  const adminKey = authToken || 'pepe123';
  const isAuthenticated = isAuth;

  // DASHBOARD STATE
  const [activeTab, setActiveTab] = useState('dashboard');

  // CALENDAR STATE
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('day'); // day, week, month

  const [citas, setCitas] = useState([]);
  const prevCitasRef = useRef(null);
  const [stats, setStats] = useState({ revenue: 0, clients: 0, latest_bookings: [] });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineBanner, setShowOfflineBanner] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);

  // [BACKUP ORIGINAL - 2026-03-23]
  // const [activeTab, setActiveTab] = useState('dashboard');
  // [CAMBIO]
  const [theme, setTheme] = useState(localStorage.getItem('dashboard-theme') || 'dark');

  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('dashboard-theme', newTheme);
  };
  const [isFutureModalOpen, setIsFutureModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- BARBEROS STATE ---
  const [barberos, setBarberos] = useState([]);
  const [barberoModal, setBarberoModal] = useState(null);
  const [bForm, setBForm] = useState({ nombre: '', activo: true, avatar: AVATAR_OPTIONS[0] });
  const [bSaving, setBSaving] = useState(false);

  // --- SERVICIOS STATE ---
  const [servicios, setServicios] = useState([]);
  const [servicioModal, setServicioModal] = useState(null);
  const [sForm, setSForm] = useState({ nombre: '', precio: '', duracion: '', descripcion: '' });
  const [sSaving, setSSaving] = useState(false);

  // --- SHARED CONFIG MODALS ---
  const [deleteModal, setDeleteModal] = useState(null);
  const [adminAvatar, setAdminAvatar] = useState(() => localStorage.getItem('admin_avatar') || AVATAR_OPTIONS[0]);
  const [adminUser, setAdminUser] = useState(() => authUser?.username || authUser?.name || localStorage.getItem('admin_user') || 'Admin');
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  // --- STATS CALCULATION ---
  const { 
    formatted: monthlyRevenueFormatted,
    formattedFuture: futureRevenueFormatted,
    futureCount: futureBookingsCount,
    futureAppointments
  } = useMonthlyRevenue(citas, servicios);

  // DETAIL MODAL STATE
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  // BLOQUEO STATE
  const [bloqueoOpen, setBloqueoOpen] = useState(false);
  const [bloqueoForm, setBloqueoForm] = useState({ fecha: new Date().toISOString().split('T')[0], hora: '12:00', motivo: 'Descanso' });
  const [bloqueoSaving, setBloqueoSaving] = useState(false);

  // CIERRE CAJA STATE
  const [cierreOpen, setCierreOpen] = useState(false);
  const [cierreData, setCierreData] = useState(null);
  const [cierreLoading, setCierreLoading] = useState(false);

  // --- ONLINE/OFFLINE DETECTION ---
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineBanner(true);
      setTimeout(() => setShowOfflineBanner(false), 3000);
      handleRefresh();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // --- LOGIN HANDLER STRONGLY TYPED ---
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user: authData.user, password: authData.pass }),
      });

      const data = await res.json();

      if (data.status === 'ok') {
        const loggedUser = data.user || authData.user;
        login({ name: loggedUser }, data.adminKey);
        setAuthError(false);
        localStorage.setItem('admin_user', loggedUser);
        if (data.avatar) {
          localStorage.setItem('admin_avatar', data.avatar);
          setAdminAvatar(data.avatar);
        }
        toast.success(`Bienvenido, ${loggedUser}`);
      } else {
        setAuthError(true);
        toast.error(data.message || "Credenciales incorrectas");
      }
    } catch (err) {
      setAuthError(true);
      toast.error("Error de conexión validando credenciales");
    }
  };

  // --- FETCH DATA CALENDAR (NEW API) ---
  const fetchCalendarData = useCallback(async () => {
    if (!isAuthenticated) return; // Guard

    try {
      // CALCULATE RANGE BASED ON VIEW
      // CALCULATE RANGE (WIDE WINDOW FOR CLIENT HISTORY)
      // Fetching +/- 6 months from current view to populate Client List while keeping Calendar responsive
      const start = subMonths(currentDate, 6);
      const end = addMonths(currentDate, 6);

      const url = `/api/admin/calendar?start=${start.toISOString()}&end=${end.toISOString()}`;
      console.log("📅 Fetching:", url);

      const res = await fetch(url, { headers: { 'X-Admin-Key': adminKey } });

      if (res.ok) {
        const data = await res.json();
        if (data.status === 'ok') {
          const parsed = (data.events || []).map(e => ({
            ...e,
            id: e.id,
            summary: e.summary,
            fecha: new Date(e.start),
            phone_guess: e.phone_guess,
            description: e.description
          }));

          if (prevCitasRef.current !== null) {
            const newAppointments = parsed.filter(p => !prevCitasRef.current.has(p.id));
            if (newAppointments.length > 0) {
              // Beep sound
              const audio = new Audio('https://cdn.pixabay.com/audio/2022/03/15/audio_c8c8a73456.mp3'); 
              audio.play().catch(e => console.log("Audio blocked by browser policy", e));
              
              newAppointments.forEach(appt => {
                toast.success(`NUEVA CITA: ${appt.summary}`, { 
                  duration: 8000, 
                  icon: '🔔',
                  style: { background: '#f59e0b', color: '#000' }
                });
              });
            }
          }
          prevCitasRef.current = new Set(parsed.map(p => p.id));

          setCitas(parsed);
          setIsOnline(true);
        }
      }
    } catch (e) {
      console.error("Fetch Error:", e);
      setIsOnline(false);
    }
  }, [currentDate, view, isAuthenticated, adminKey]);

  // --- FETCH STATS (OLD API FOR KPIS) ---
  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return; // Guard
    try {
      const res = await fetch('/api/admin/stats', { headers: { 'X-Admin-Key': adminKey } });
      if (res.ok) {
        const data = await res.json();
        setStats({
          revenue: data.estimated_revenue || 0,
          clients: data.total_clients || 0,
          future_bookings: data.future_bookings || 0,
          latest_bookings: data.latest_bookings || []
        });
      }
    } catch { }
  }, [isAuthenticated, adminKey]);

  const fetchServicios = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch('/api/admin/servicios', { headers: { 'X-Admin-Key': adminKey } });
      if (res.ok) {
        const data = await res.json();
        setServicios(data.servicios || []);
      }
    } catch { }
  }, [isAuthenticated, adminKey]);

  const fetchBarberos = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch('/api/admin/barberos', { headers: { 'X-Admin-Key': adminKey } });
      if (res.ok) {
        const data = await res.json();
        setBarberos(data.barberos || []);
      }
    } catch { }
  }, [isAuthenticated, adminKey]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchCalendarData(),
        fetchStats(),
        fetchServicios(),
        fetchBarberos()
      ]);
      setLastUpdated(new Date());
      toast.success("Datos actualizados correctamente");
    } catch (error) {
      console.error('Error refreshing:', error);
      toast.error('Error al actualizar datos');
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchCalendarData, fetchStats, fetchServicios, fetchBarberos, isRefreshing]);

  const getTimeAgo = (date) => {
    const minutes = Math.floor((new Date() - date) / 60000);
    if (minutes < 1) return 'ahora mismo';
    if (minutes === 1) return 'hace 1 minuto';
    if (minutes < 60) return `hace ${minutes} minutos`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return 'hace 1 hora';
    return `hace ${hours} horas`;
  };

  const showToast = (message, type) => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCalendarData();
      fetchStats();
      fetchServicios();
      fetchBarberos();
      const interval = setInterval(() => {
        fetchCalendarData();
        fetchStats();
        setLastUpdated(new Date());
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchCalendarData, fetchStats, fetchServicios, fetchBarberos, isAuthenticated]);

  // HANDLERS
  const handleNavigate = (direction) => {
    if (view === 'day') setCurrentDate(d => direction === 'next' ? addDays(d, 1) : subDays(d, 1));
    if (view === 'month') setCurrentDate(d => direction === 'next' ? addMonths(d, 1) : subMonths(d, 1));
    if (view === 'week') setCurrentDate(d => direction === 'next' ? addWeeks(d, 1) : subWeeks(d, 1));
  };

  const handleCreateBooking = async (payload) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.status === 'ok') {
        toast.success("Cita creada correctamente");
        setIsModalOpen(false);
        fetchCalendarData(); // Refresh calendar immediately
      } else {
        toast.error(json.message || "Error al crear cita");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventClick = (cita) => {
    setSelectedEvent(cita);
    setIsDetailOpen(true);
  };

  const handleCancelBooking = async (cita) => {
    if (!cita || !cita.id) return;

    setIsCanceling(true);
    try {
      const res = await fetch('/api/cancel-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: cita.id,
          phone: cita.phone_guess || "000000000"
        })
      });
      const json = await res.json();
      if (json.status === 'ok') {
        toast.success("Cita cancelada correctamente");
        setIsDetailOpen(false);
        fetchCalendarData();
      } else {
        toast.error("No se pudo cancelar la cita");
      }
    } catch (e) {
      toast.error("Error al conectar con el servidor");
    } finally {
      setIsCanceling(false);
    }
  };

  // --- CONDITIONAL RETURN: LOGIN SCREEN ---
  if (isAuthLoading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white font-sans">Cargando sistema...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-zinc-950 text-white font-sans p-4">
        <div className="w-full max-w-md space-y-8 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-10 backdrop-blur-xl shadow-2xl animate-in fade-in zoom-in duration-500">
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 mb-4 ring-1 ring-amber-500/50">
              <UserCog className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-3xl font-black tracking-tight text-white font-[var(--font-brand)]">
              PANEL <span className="text-amber-500">ADMIN</span>
            </h2>
            <p className="mt-2 text-sm text-zinc-400">Acceso restringido a personal autorizado</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 font-medium">Usuario</Label>
                <Input
                  type="text"
                  value={authData.user}
                  onChange={e => setAuthData({ ...authData, user: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus:border-amber-500 h-12 text-lg mt-1 text-white"
                  placeholder="Usuario"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-zinc-400 font-medium">Contraseña</Label>
                <Input
                  type="password"
                  value={authData.pass}
                  onChange={e => setAuthData({ ...authData, pass: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 focus:border-amber-500 h-12 text-lg mt-1 text-white"
                  placeholder="•••••••"
                />
              </div>
            </div>

            {authError && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm text-center font-medium animate-pulse flex items-center justify-center gap-2">
                <Ban className="w-4 h-4" /> Credenciales incorrectas
              </div>
            )}

            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold h-12 text-lg shadow-[0_0_20px_rgba(245,158,11,0.2)] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all">
              ENTRAR AL SISTEMA
            </Button>
          </form>

          <div className="text-center pt-4 border-t border-zinc-800/50">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest">Secure System V4.0</p>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDERERS ---

  const renderDashboard = () => (
    <div className="mx-auto max-w-[1600px] space-y-6 pb-20">
      <Toaster position="top-right" theme={theme} />
      {/* KPI GRID */}
      {/* KPI GRID (VISUAL MATCH TO IMAGE) */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* CARD 1: INGRESOS REALIZADOS (GREEN) */}
        <KPICard
          title="INGRESOS REALIZADOS"
          value={monthlyRevenueFormatted}
          subtitle="Dinero en caja"
          isLoading={isRefreshing}
          onClick={() => setActiveTab('agenda')}
        >
          {/* Green Wave Decoration */}
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-emerald-500/10 to-transparent overflow-hidden">
            <svg className="absolute bottom-0 w-[110%] h-full text-emerald-500/20 animate-wave" viewBox="0 0 1440 320" preserveAspectRatio="none">
              <path fill="currentColor" d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,250.7C1248,256,1344,288,1392,304L1440,320L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
          <div className="income-flow-animation" />
        </KPICard>

        {/* CARD 2: PROYECCIÓN FUTURA (GOLD) */}
        <KPICard
          title="PROYECCIÓN FUTURA"
          value={futureRevenueFormatted}
          subtitle="Citas agendadas"
          icon={TrendingUp}
          isLoading={isRefreshing}
          variant="warning"
          onClick={() => setIsFutureModalOpen(true)}
        >
          <div className="mt-4 flex items-center gap-2 text-xs text-amber-500">
            <Activity className="h-3 w-3" /> {futureBookingsCount} citas futuras
          </div>
        </KPICard>

        {/* CARD 3: OCUPACIÓN HOY (PROGRESS) */}
        <KPICard
          title="CLIENTES REGISTRADOS"
          value={stats.clients || 0}
          subtitle="En la base de datos"
          icon={Users}
          isLoading={isRefreshing}
          onClick={() => setActiveTab('clientes')}
        >
          {/* Progress Bar */}
          <div className="h-2 w-full bg-[var(--bg-card-inner)] rounded-full overflow-hidden mt-4">
            <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (stats.clients || 0) * 5)}%` }} />
          </div>
        </KPICard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* AGENDA CENTRAL */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[var(--text-main)]">Agenda</h2>
            <Button onClick={() => setIsModalOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
              <Plus className="mr-2 h-4 w-4" /> Nueva Reserva
            </Button>
          </div>

          <CalendarHeader
            currentDate={currentDate}
            view={view}
            onViewChange={setView}
            onNavigate={handleNavigate}
            onToday={() => setCurrentDate(new Date())}
          />

          {isRefreshing && citas.length === 0 ? (
            <SkeletonCalendar />
          ) : (
            <TimelineCalendar
              citas={citas}
              currentDate={currentDate}
              view={view}
              onSlotClick={() => setIsModalOpen(true)}
              onEventClick={handleEventClick}
            />
          )}
        </div>

        {/* ACTION SIDEBAR (VISUAL MATCH TO IMAGE) */}
        <div className="lg:col-span-4 space-y-6">
          <TopServices appointments={citas} services={servicios} />

          {/* ACCIONES RÁPIDAS */}
          <div className="rounded-xl border border-[var(--border-main)] bg-[var(--bg-card)] p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
            <h3 className="mb-4 text-sm font-bold text-[var(--text-main)]">Acciones Rápidas</h3>
            <div className="space-y-3">
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/10 hover:bg-amber-500/20 transition-all group text-left"
              >
                <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center text-black shadow-lg shadow-amber-500/20 group-hover:scale-105 transition-transform">
                  <Plus className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold text-[var(--text-main)]">Nueva Reserva</p>
                  <p className="text-xs text-[var(--text-muted)]">Walk-in</p>
                </div>
              </button>

              <button className="w-full flex items-center gap-3 p-3 rounded-lg border border-[var(--border-main)] bg-[var(--bg-card-inner)] hover:bg-[var(--bg-item-hover)] transition-all group text-left" onClick={() => setBloqueoOpen(true)}>
                <div className="h-10 w-10 rounded-lg bg-[var(--bg-card-inner)] flex items-center justify-center text-[var(--text-muted)] group-hover:text-[var(--text-main)] transition-colors">
                  <Ban className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-secondary)]">Bloquear Hora</p>
                  <p className="text-xs text-[var(--text-muted)]">Descanso</p>
                </div>
              </button>

              <button className="w-full flex items-center gap-3 p-3 rounded-lg border border-[var(--border-main)] bg-[var(--bg-card-inner)] hover:bg-[var(--bg-item-hover)] transition-all group text-left" onClick={async () => { setCierreLoading(true); setCierreOpen(true); try { const r = await fetch(`/api/admin/cierre-caja?date=${new Date().toISOString().split('T')[0]}`, { headers: { 'X-Admin-Key': adminKey } }); const d = await r.json(); if (d.status === 'ok') setCierreData(d); } catch { } setCierreLoading(false); }}>
                <div className="h-10 w-10 rounded-lg bg-[var(--bg-card-inner)] flex items-center justify-center text-[var(--text-muted)] group-hover:text-[var(--text-main)] transition-colors">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-secondary)]">Cerrar Caja</p>
                  <p className="text-xs text-[var(--text-muted)]">Fin del día</p>
                </div>
              </button>
            </div>
          </div>

          {/* ACTIVIDAD RECIENTE */}
          <div className="rounded-xl border border-[var(--border-main)] bg-[var(--bg-card)] p-6" style={{ boxShadow: 'var(--shadow-card)' }}>
            <h3 className="mb-4 text-sm font-bold text-[var(--text-main)]">Actividad Reciente</h3>
            <div className="space-y-3">
              {(() => {
                let activas = citas.filter(c => isToday(c.fecha)).sort((a, b) => a.fecha - b.fecha);
                if (activas.length === 0) {
                  activas = citas.filter(c => isAfter(c.fecha, new Date())).sort((a, b) => a.fecha - b.fecha).slice(0, 5);
                }
                
                // Fallback: Si no hay hoy ni futuras, mostrar las 5 últimas pasadas
                if (activas.length === 0) {
                  activas = citas.filter(c => isBefore(c.fecha, new Date())).sort((a, b) => b.fecha - a.fecha).slice(0, 5);
                }

                if (activas.length === 0) {
                  return <p className="text-sm text-zinc-500 italic text-center py-4">Sin actividad reciente</p>;
                }

                return activas.map(cita => {
                  const isPasada = isBefore(cita.fecha, new Date());
                  const fechaFormat = isToday(cita.fecha) ? format(cita.fecha, 'HH:mm') : format(cita.fecha, 'dd/MM HH:mm');
                  return (
                    <div key={cita.id} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border-main)] bg-[var(--bg-card-inner)]">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center border ${isPasada ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/20'}`}>
                        {isPasada ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--text-main)] truncate">{cita.summary}</p>
                        <p className="text-xs text-[var(--text-muted)] truncate">
                          {isPasada ? 'Completado' : 'Pendiente'} · {cita.summary.split(' - ')[0] || 'kennedy'}
                        </p>
                      </div>
                      <span className="text-[10px] text-[var(--text-muted)] font-mono whitespace-nowrap">{fechaFormat}</span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`theme-container ${theme} flex h-screen w-full bg-[var(--bg-app)] text-[var(--text-main)] font-sans transition-colors duration-300`}>
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} adminAvatar={adminAvatar} setAvatarModalOpen={setAvatarModalOpen} />
      <main className="flex-1 pl-[72px] flex flex-col h-screen overflow-hidden">
        {/* Banner Offline */}
        {showOfflineBanner && (
          <div className={`fixed top-0 left-0 right-0 z-[100] px-4 py-2 text-center text-sm font-medium transition-all duration-500 animate-in slide-in-from-top ${
            isOnline ? 'bg-emerald-600' : 'bg-red-600'
          }`}>
            <div className="flex items-center justify-center gap-2 text-white">
              {isOnline ? <WifiIcon size={16} /> : <WifiOffIcon size={16} />}
              {isOnline ? 'Conexión restablecida' : 'SIN CONEXIÓN - MODO OFFLINE'}
            </div>
          </div>
        )}

        <header className="flex items-center justify-between border-b border-[var(--border-main)] bg-[var(--bg-header)] px-6 py-4 backdrop-blur-sm transition-colors duration-300">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-[var(--text-main)] flex items-center gap-2">
                Buen turno, <span className="text-[var(--text-accent)]">{adminUser}</span>
              </h1>
              <p className="mt-0.5 text-xs text-[var(--text-muted)] lowercase first-letter:capitalize">
                {format(new Date(), "EEEE, d 'de' MMMM", { locale: es })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* [CAMBIO - 2026-03-23] Botón Modo Día/Noche */}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleTheme}
              className="border-[var(--border-main)] bg-[var(--bg-card)] hover:bg-[var(--bg-item-hover)] transition-all duration-300"
              title={theme === 'dark' ? 'Cambiar a modo día' : 'Cambiar a modo noche'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4 text-amber-500" /> : <Moon className="h-4 w-4 text-indigo-500" />}
            </Button>

            {/* Indicador última actualización */}
            <div className="hidden md:flex flex-col items-end mr-2">
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-bold">Última actualización</span>
              <span className="text-xs text-[var(--text-secondary)]">{getTimeAgo(lastUpdated)}</span>
            </div>

            {/* Botón Refresh */}
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              variant="outline"
              size="sm"
              className={`
                h-10 gap-2 border-[var(--border-main)] bg-[var(--bg-card)] text-[var(--text-secondary)] transition-all hover:bg-[var(--bg-item-hover)] hover:text-[var(--text-main)]
                ${isRefreshing ? 'opacity-70 cursor-not-allowed' : 'active:scale-95'}
              `}
            >
              <RefreshIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-amber-500' : ''}`} />
              <span className="hidden sm:inline">{isRefreshing ? 'Actualizando...' : 'Actualizar'}</span>
            </Button>

            {/* Indicador Sistema Online */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--bg-card)] border border-[var(--border-main)] text-[10px] font-bold">
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></span>
              <span className={isOnline ? 'text-emerald-500' : 'text-red-500'}>
                {isOnline ? 'SISTEMA ONLINE' : 'DESCONECTADO'}
              </span>
            </div>

            <div className="h-6 w-px bg-[var(--border-main)] mx-1 hidden md:block" />

            {/* Botón Logout */}
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-zinc-500 hover:text-white hover:bg-zinc-800"
              title="Cerrar sesión"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rotate-180">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
            </Button>
          </div>
        </header>

        <ScrollArea className="flex-1 p-6">
          {activeTab === 'dashboard' ? renderDashboard() : null}
          {activeTab === 'agenda' ? (
            <div className="max-w-[1200px] mx-auto">
              <CalendarHeader currentDate={currentDate} view={view} onViewChange={setView} onNavigate={handleNavigate} onToday={() => setCurrentDate(new Date())} />
              <TimelineCalendar
                citas={citas}
                currentDate={currentDate}
                view={view}
                onSlotClick={() => setIsModalOpen(true)}
                onEventClick={handleEventClick}
              />
            </div>
          ) : null}
          {activeTab === 'configuracion' ? <ConfigurationView
            adminKey={adminKey}
            barberos={barberos}
            fetchBarberos={fetchBarberos}
            barberoModal={barberoModal}
            setBarberoModal={setBarberoModal}
            deleteModal={deleteModal}
            setDeleteModal={setDeleteModal}
            bForm={bForm}
            setBForm={setBForm}
            bSaving={bSaving}
            setBSaving={setBSaving}
            servicios={servicios}
            fetchServicios={fetchServicios}
            servicioModal={servicioModal}
            setServicioModal={setServicioModal}
            sForm={sForm}
            setSForm={setSForm}
            sSaving={sSaving}
            setSSaving={setSSaving}
          /> : null}
          {activeTab === 'clientes' ? <ClientsView citas={citas} servicios={servicios} /> : null}
        </ScrollArea>

        <NuevaReservaModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          barberos={BARBEROS}
          servicios={servicios}
          onConfirm={handleCreateBooking}
          isSubmitting={isSubmitting}
        />

        <DetalleCitaModal
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          cita={selectedEvent}
          onCancel={handleCancelBooking}
          isCanceling={isCanceling}
        />

        {/* MODAL BLOQUEAR HORA */}
        {bloqueoOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setBloqueoOpen(false)}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2"><Ban className="h-5 w-5 text-amber-500" /> Bloquear Hora</h3>
              <p className="text-xs text-zinc-500 mb-4">Bloquea 1 hora para que los clientes no puedan reservar.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400">Fecha</Label>
                  <Input type="date" value={bloqueoForm.fecha} onChange={e => setBloqueoForm(f => ({ ...f, fecha: e.target.value }))} className="bg-zinc-950 border-zinc-800 focus:border-amber-500 h-11 mt-1 text-white" />
                </div>
                <div>
                  <Label className="text-zinc-400">Hora</Label>
                  <select value={bloqueoForm.hora} onChange={e => setBloqueoForm(f => ({ ...f, hora: e.target.value }))} className="w-full h-11 mt-1 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-white focus:border-amber-500 outline-none">
                    {Array.from({ length: 22 }, (_, i) => { const h = 10 + Math.floor(i / 2); const m = (i % 2) * 30; if (h === 15 && m === 0) return null; if (h >= 21) return null; const t = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`; return <option key={t} value={t}>{t}</option>; })}
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-400">Motivo</Label>
                  <Input value={bloqueoForm.motivo} onChange={e => setBloqueoForm(f => ({ ...f, motivo: e.target.value }))} className="bg-zinc-950 border-zinc-800 focus:border-amber-500 h-11 mt-1 text-white" placeholder="Descanso, Almuerzo..." />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setBloqueoOpen(false)}>Cancelar</Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold" disabled={bloqueoSaving} onClick={async () => {
                  setBloqueoSaving(true);
                  try {
                    const r = await fetch('/api/admin/bloqueos', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey }, body: JSON.stringify(bloqueoForm) });
                    const d = await r.json();
                    if (d.status === 'ok') { toast.success(`Hora ${bloqueoForm.hora} bloqueada`); setBloqueoOpen(false); } else { toast.error(d.message || 'Error'); }
                  } catch { toast.error('Error de conexión'); }
                  setBloqueoSaving(false);
                }}>
                  {bloqueoSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Bloquear'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL CERRAR CAJA */}
        {cierreOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { setCierreOpen(false); setCierreData(null); }}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5 text-amber-500" /> Cierre de Caja</h3>
              {cierreLoading ? (
                <div className="flex items-center justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-amber-500" /></div>
              ) : cierreData ? (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">Resumen del día: <span className="text-white font-bold">{cierreData.fecha}</span></p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">{cierreData.totalCitas}</p>
                      <p className="text-xs text-zinc-500 mt-1">Total Citas</p>
                    </div>
                    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-500">{cierreData.completadas}</p>
                      <p className="text-xs text-zinc-500 mt-1">Completadas</p>
                    </div>
                    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-amber-500">{cierreData.pendientes}</p>
                      <p className="text-xs text-zinc-500 mt-1">Pendientes</p>
                    </div>
                    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">{cierreData.ingresoEstimado?.toFixed(2)}€</p>
                      <p className="text-xs text-zinc-500 mt-1">Ingreso Est.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-zinc-400 text-center py-4">No hay datos disponibles.</p>
              )}
              <div className="mt-6">
                <Button variant="outline" className="w-full border-zinc-700" onClick={() => { setCierreOpen(false); setCierreData(null); }}>Cerrar</Button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL AVATAR ADMIN */}
        {avatarModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setAvatarModalOpen(false)}>
            <div className="bg-[var(--bg-card)] border-[var(--border-main)] rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-[var(--text-main)] font-bold text-lg mb-4 text-center">Elige tu Avatar</h3>
              <div className="flex flex-wrap gap-4 justify-center py-4">
                {AVATAR_OPTIONS.map(url => (
                  <img
                    key={url}
                    src={url}
                    className={`h-14 w-14 rounded-full border-2 border-[var(--border-main)] hover:border-amber-500 cursor-pointer transition-all hover:scale-110 ${adminAvatar === url ? 'border-amber-500 scale-110' : ''}`}
                    onClick={() => {
                      setAdminAvatar(url);
                      localStorage.setItem('admin_avatar', url);
                      setAvatarModalOpen(false);
                      toast.success('Avatar actualizado');
                    }}
                    alt="avatar"
                  />
                ))}
              </div>
              <Button variant="outline" className="w-full mt-2 border-[var(--border-main)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]" onClick={() => setAvatarModalOpen(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {/* MODAL CITAS FUTURAS */}
        <Dialog open={isFutureModalOpen} onOpenChange={setIsFutureModalOpen}>
          <DialogContent className="border-[var(--border-main)] bg-[var(--bg-card)] text-[var(--text-main)] sm:max-w-[600px] w-full max-h-[80vh] flex flex-col shadow-[var(--shadow-lg)]">
            <DialogHeader>
              <DialogTitle className="text-[var(--text-main)]">Proyección Futura - Detalle de Citas</DialogTitle>
              <DialogDescription className="text-[var(--text-secondary)]">
                Listado de las siguientes {futureAppointments.length} citas agendadas confirmadas.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="flex-1 -mx-6 px-6 relative mt-4" style={{ maxHeight: '60vh' }}>
              <div className="space-y-4">
                {futureAppointments.length > 0 ? (
                  futureAppointments.map((b) => (
                    <div key={b.id} className="p-4 rounded-xl border border-[var(--border-main)] bg-[var(--bg-card-inner)] flex flex-col gap-2">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-amber-500" />
                          <span className="font-bold text-[var(--text-main)] text-lg">
                            {b.summary.replace('Cita: ', '').split(' - ')[0]}
                          </span>
                        </div>
                        <span className="text-[var(--text-secondary)] text-sm flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {b.fecha ? new Date(b.fecha).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Fecha desconocida'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2">
                        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                          <Phone className="w-4 h-4 text-[var(--text-muted)]" />
                          {b.phone_guess || 'Sin teléfono'}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                          <Scissors className="w-4 h-4 text-[var(--text-muted)]" />
                          {b.summary}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-[var(--text-muted)]">No hay citas futuras registradas</div>
                )}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

/*
---
## 📝 Log de Cambios (Auto-generado)
- [2026-03-23] Modificación en `AdminDashboard.jsx`: Implementación de Modo Claro/Oscuro mediante variables CSS y Toggle en Header.
- [2026-03-23] Impacto: Dashboard (Kennedy/Pepe)
- [2026-03-23] Validado: Sí
---
*/
